import { CommentStatus } from '@/@types/comment'
import type { ContentType } from '@/@types/common'
import type { CommentNotification as CommentNotificationType } from '@/@types/notification'
import ClickableStackAvatarGroup from '@/components/ClickableStackAvatarGroup'
import ScreenNames from '@/components/ScreenNames'
import { CONTENT_LOCALE } from '@/constants/locale'
import { formatAsRelativeTime } from '@/utils/day'
import { getCommentQueryOptions } from '@/utils/fetchers/comment'
import useNotificationClickHandler from '@/utils/hooks/useNotificationClickHandler'
import queryClient from '@/utils/queryClient'
import { css } from '@emotion/react'
import { modals } from '@mantine/modals'
import { AxiosError } from 'axios'
import { type HTMLAttributes } from 'react'
import toast from 'react-hot-toast'
import Button from '../Button'
import { showCommentFormModal } from '../Comment/utils'
import ContentContainer from '../ContentContainer'
import ViewPictureText from '../Picture/ViewPictureText'
import NotificationContainer from './NotificationContainer'
import NotificationReference from './NotificationReference'

export interface CommentNotificationProps extends HTMLAttributes<HTMLDivElement> {
	data: CommentNotificationType
	type: ContentType
	noReplyButton?: boolean
}

export default function CommentNotification({
	data,
	type: contentType,
	noReplyButton = false,
	...props
}: CommentNotificationProps) {
	const {
		actionItem: { users, usersCount, content, status, pictures },
		referenceItem,
		createdAt,
	} = data
	const isDeleted = status === CommentStatus.DELETED
	const { handleClick, isClickable } = useNotificationClickHandler(data.linkUrl)

	return (
		<>
			<NotificationContainer
				onClick={handleClick}
				isClickable={isClickable}
				css={css`
					display: grid;
					grid-template-columns: 1fr auto;
					gap: 12px;
				`}
				{...props}
			>
				<div
					css={css`
						display: flex;
						flex-direction: column;
						gap: 8px;
					`}
				>
					<div
						css={css`
							display: grid;
							grid-template-columns: auto 1fr;
							gap: 12px;
							overflow: hidden;
						`}
					>
						<ClickableStackAvatarGroup data={users} />

						<div
							css={css`
								display: flex;
								flex-direction: column;
								gap: 4px;
							`}
						>
							<header
								css={css`
									display: flex;
									align-items: center;
									gap: 12px;
								`}
							>
								<div
									css={css`
										line-height: 24px;
									`}
								>
									<ScreenNames
										data={users.slice(0, 1)}
										css={css`
											margin-right: 4px;
										`}
									/>
									<span
										css={css`
											color: var(--tint-secondary);
										`}
									>
										{contentType === 'COMMENT' ? '回复' : '评论'}了你的{CONTENT_LOCALE[contentType]}
									</span>
								</div>
							</header>

							<main
								css={css`
									overflow-wrap: anywhere;
									display: flex;
									align-items: center;
									gap: 0.5rem;
								`}
							>
								<ContentContainer
									isDeleted={isDeleted}
									css={css`
										display: flex;
										align-items: center;
										gap: 0.5rem;
									`}
								>
									{content}
									{pictures && pictures.length > 0 && <ViewPictureText data={pictures[0]} />}
								</ContentContainer>
							</main>
						</div>
					</div>
					<footer
						css={css`
							color: var(--tint-tertiary);
							font-size: 12px;
							line-height: 18px;
							display: flex;
							gap: 8px;
							align-items: center;
						`}
					>
						<span>
							{/* datetime */}
							{formatAsRelativeTime(createdAt)}
						</span>
						{!noReplyButton && !isDeleted && (
							<Button
								onClick={async (e) => {
									e.stopPropagation()
									const [comment] = await Promise.all([
										queryClient.ensureQueryData(
											getCommentQueryOptions({
												id: data.actionItem.id,
												targetType: data.actionItem.targetType,
											})
										),
									]).catch((err: AxiosError<{ error: string }>) => {
										if (err.response?.data?.error) {
											toast.error(err.response.data.error)
										}
										throw err
									})

									const modalId = showCommentFormModal({
										replyToComment: {
											to: comment,
											threadId: comment.threadId,
										},
										onClose: () => {
											modals.close(modalId)
										},
									})
								}}
								appearance="text"
								size="sm"
								className="jk-link-text"
								css={css`
									&.jk-btn {
										padding: 0;
										height: fit-content;
										min-width: unset;
										color: inherit;
										font-size: inherit;
										line-height: inherit;
										border-radius: unset;
									}
								`}
							>
								回复
							</Button>
						)}
					</footer>
				</div>

				<NotificationReference data={referenceItem} />
			</NotificationContainer>
		</>
	)
}
